import {
  useInstallIntegration,
  usePatchIntegration
} from "@/api/useIntegrations"
import { INTEGRATION_VENDORS } from "@/const/vendors-constants"

import type {
  DockerHubInstallation,
  DockerHubInstallInput,
  DockerHubUpdateInput
} from "@/api/useIntegrations.types"
import type { ActionsIntegration } from "./useIntegration.types"

export interface DockerHubFormValues {
  username?: string
  accessToken?: string
  repositories?: string[]
}

type DockerHubAPIProps = Omit<ActionsIntegration, "onRemove"> & {
  account?: DockerHubInstallation
}

const MESSAGE_PREFIX = "integrations.dockerHub.messages"

export const useDockerHubAPI = (props: DockerHubAPIProps) => {
  const { account, t, message: messageApi } = props
  const vendor = INTEGRATION_VENDORS.dockerHub
  const formId = account?.accountId || `${vendor}-form`
  const installer = useInstallIntegration({ vendor })
  const updater = usePatchIntegration({ vendor })

  const onFinish = async (values: DockerHubFormValues) => {
    const prefix = `${MESSAGE_PREFIX}.${account ? "update" : "add"}`
    const repositories = values.repositories || []
    const accessToken = values.accessToken?.trim() || undefined

    try {
      if (account) {
        const dockerHub: DockerHubUpdateInput = {
          accountId: account.accountId,
          repositories,
          accessToken
        }

        await updater.mutateAsync({ data: { dockerHub } })
      } else {
        const dockerHub: DockerHubInstallInput = {
          username: values.username?.trim() || "",
          accessToken: accessToken || "",
          repositories
        }

        await installer.mutateAsync({ data: { dockerHub } })
      }
    } catch (error: any) {
      messageApi?.error(error?.data?.displayMessage || t(`${prefix}.failed`))

      return
    }

    messageApi?.success(t(`${prefix}.success`))

    if (account) {
      props.form.setFieldValue("accessToken", undefined)
      props.setIsDirty(false)
    } else props.backToIntegration(true)
  }

  return {
    formId,
    onFinish,
    isPending: installer.isPending || updater.isPending
  }
}
